import { makeStyles } from '@material-ui/core/styles';
import { Hidden, Link, Stack } from '@mui/material';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import ReactGA from 'react-ga4';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import RecommendedArticleWidget from '../Components/RecommendedArticleWidget';
import { BASE_API_PATH, BLOG_PATH } from '../constants';

const useStyles = makeStyles((theme) => ({
    centeredContent: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        maxWidth: 1000,
        [theme.breakpoints.down('md')]: {
            width: '90%',
        },
        margin: 'auto',
        flexDirection: 'column',
    },
    image: {
        width: '100%',
        maxWidth: '100%',
        [theme.breakpoints.down('md')]: {
            width: '100%',
        },
        display: 'block',
        paddingBottom: 5,
    },
    imageWrapper: {
        width: '100%',
        borderRadius: 14,
        overflow: 'hidden',
    },
    title: {
        fontSize: 30,
    },
}));

interface ImageWithCaptionProps {
    url: string;
    caption: string;
}

function ImageWithCaption(props: ImageWithCaptionProps) {
    const classes = useStyles();

    return (
        <Box className={classes.centeredContent} paddingBottom={2}>
            <Box className={classes.imageWrapper}>
                <img src={props.url} alt={props.url} className={classes.image} />
            </Box>
            <Typography
                variant="h4"
                lineHeight={1.5}
                fontSize={15}
                textAlign={'center'}
                fontStyle={'italic'}
                color="#55729f"
                maxWidth={'100%'}
            >
                {props.caption}
            </Typography>
        </Box>
    );
}

interface ParagraphProps {
    text: string;
}

interface TextComponent {
    type: 'string' | 'link';
    text: string;
    link?: string;
}

interface ParagraphSegment {
    type: 'paragraph' | 'unordered_list' | 'ordered_list';
    text?: string;
    items?: string[];
}

interface SuggestedPost {
    id: string;
    title: string;
    imageUrl: string;
    description: string;
    category: string;
    timestamp: number;
    href: string;
}

interface FeedItem {
    id: string;
    title: string;
    summary: string;
    timestamp: number;
    imageUrl: string;
    contentType: 'blog' | 'photo_collection';
    href?: string;
}

interface FeedResponse {
    items: FeedItem[];
}

function suggestedArticles(suggestedPosts: SuggestedPost[], direction: string) {
    const stackDirection = direction === 'column' ? 'column' : 'row';

    return (
        <Stack direction={stackDirection} justifyContent="flex-start" alignItems="stretch" spacing={2} paddingBottom={2}>
            {suggestedPosts.map((suggestedPost) => (
                <RecommendedArticleWidget
                    key={suggestedPost.href}
                    id={suggestedPost.id}
                    href={suggestedPost.href}
                    title={suggestedPost.title}
                    imageUrl={suggestedPost.imageUrl}
                    description={suggestedPost.description}
                    category={suggestedPost.category}
                    timestamp={suggestedPost.timestamp}
                />
            ))}
        </Stack>
    );
}

function Paragraph(props: ParagraphProps) {
    const segments = parseParagraphSegments(props.text);
    return (
        <Typography
            component="div"
            variant="h4"
            lineHeight={1.5}
            fontSize={20}
            paddingBottom={3}
            color="#2f5487"
        >
            {segments.map((segment, segmentIndex) => {
                if (segment.type === 'paragraph') {
                    return (
                        <Box
                            key={`paragraph-${segmentIndex}`}
                            component="p"
                            sx={{ margin: 0, marginBottom: 2, whiteSpace: 'pre-line' }}
                        >
                            {renderInlineText(segment.text || '')}
                        </Box>
                    );
                }

                const listItems = segment.items || [];
                return (
                    <Box
                        key={`list-${segment.type}-${segmentIndex}`}
                        component={segment.type === 'ordered_list' ? 'ol' : 'ul'}
                        sx={{ margin: 0, marginBottom: 2, paddingLeft: '1.5em' }}
                    >
                        {listItems.map((item, itemIndex) => (
                            <Box key={`list-item-${itemIndex}`} component="li" sx={{ marginBottom: 0.5 }}>
                                {renderInlineText(item)}
                            </Box>
                        ))}
                    </Box>
                );
            })}
        </Typography>
    );
}

interface BlogBodyElement {
    type: string;
    text: string;
    mediaKey?: string;
    imageUrl?: string;
}

interface BlogContent {
    title: string;
    timestamp: number;
    category?: string;
    body: BlogBodyElement[];
}

function isImage(element: BlogBodyElement): element is BlogBodyElement & { type: string; imageUrl: string } {
    return Boolean(element.imageUrl);
}

function useQuery() {
    return new URLSearchParams(useLocation().search);
}

function timestampToHumanReadableDate(timestamp: number) {
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });
}

type PostCategory = 'Reflections' | 'Life' | 'Projects';

function inferCategoryFromTitleAndText(title: string, text: string): PostCategory {
    const fullText = `${title} ${text}`.toLowerCase();
    if (/(project|build|app|feature|tool|algorithm|website)/i.test(fullText)) {
        return 'Projects';
    }
    if (/(learn|lesson|growth|reflection|mindset|habit)/i.test(fullText)) {
        return 'Reflections';
    }
    if (/(life|routine|personal|journal|weekend|moving)/i.test(fullText)) {
        return 'Life';
    }
    return 'Life';
}

function renderInlineText(text: string) {
    return parseParagraph(text).map((component, index) => {
        if (component.type === 'string') {
            return <Box component="span" key={`text-${index}`}>{component.text}</Box>;
        }
        return (
            <Link
                key={`link-${index}`}
                href={component.link}
                underline="always"
                color="#1d4f9e"
                target="_blank"
                rel="noopener"
                sx={{
                    fontWeight: 600,
                    textUnderlineOffset: '2px',
                    textDecorationThickness: '2px',
                    '&:hover': {
                        color: '#163f86',
                        textDecorationThickness: '3px',
                    },
                    '&:focus-visible': {
                        outline: '2px solid #7da8e8',
                        outlineOffset: '2px',
                        borderRadius: '2px',
                    },
                }}
            >
                {component.text}
            </Link>
        );
    });
}

function parseParagraphSegments(input: string): ParagraphSegment[] {
    const segments: ParagraphSegment[] = [];
    const paragraphLines: string[] = [];
    let listType: ParagraphSegment['type'] | null = null;
    let listItems: string[] = [];

    const flushParagraph = () => {
        const paragraphText = paragraphLines.join('\n').trim();
        if (paragraphText) {
            segments.push({ type: 'paragraph', text: paragraphText });
        }
        paragraphLines.length = 0;
    };

    const flushList = () => {
        if (listType && listItems.length > 0) {
            segments.push({ type: listType, items: [...listItems] });
        }
        listType = null;
        listItems = [];
    };

    for (const rawLine of input.split('\n')) {
        const trimmed = rawLine.trim();
        if (!trimmed) {
            flushParagraph();
            flushList();
            continue;
        }

        const unorderedMatch = trimmed.match(/^[-*]\s+(.+)$/);
        const orderedMatch = trimmed.match(/^\d+\.\s+(.+)$/);
        if (unorderedMatch || orderedMatch) {
            flushParagraph();
            const nextListType: ParagraphSegment['type'] = unorderedMatch ? 'unordered_list' : 'ordered_list';
            const itemText = (unorderedMatch?.[1] || orderedMatch?.[1] || '').trim();
            if (listType && listType !== nextListType) {
                flushList();
            }
            listType = nextListType;
            listItems.push(itemText);
            continue;
        }

        flushList();
        paragraphLines.push(rawLine);
    }

    flushParagraph();
    flushList();
    return segments;
}

const parseParagraph = (input: string): TextComponent[] => {
    const regex = /\[([^\]]+)\]\(([^)]+)\)/g;
    const result: TextComponent[] = [];
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(input)) !== null) {
        if (match.index > lastIndex) {
            result.push({ type: 'string', text: input.substring(lastIndex, match.index) });
        }

        const label = match[1].trim();
        const link = match[2].trim();
        result.push({ type: 'link', text: label, link });
        lastIndex = regex.lastIndex;
    }

    if (lastIndex < input.length) {
        result.push({ type: 'string', text: input.substring(lastIndex) });
    }

    return result;
};

export function BlogPost() {
    const classes = useStyles();
    const query = useQuery();
    const blogPostId = query.get('id');

    const [content, setContent] = useState<BlogContent>({
        title: 'Blog',
        timestamp: 0,
        body: [],
    });
    const [loadingPostContent, setLoadingPostContent] = useState(true);
    const [error, setError] = useState(false);
    const [suggestedPosts, setSuggestedPosts] = useState<SuggestedPost[]>([]);

    useEffect(() => {
        const fetchBlogPosts = async () => {
            try {
                const response = await fetch(`${BASE_API_PATH}/blog/${blogPostId}`);
                const data: BlogContent = await response.json();
                setContent(data);
                setLoadingPostContent(false);
            } catch (error) {
                setError(true);
                setLoadingPostContent(false);
            }
        };
        fetchBlogPosts();
        window.scrollTo({ top: 0, behavior: 'auto' });
    }, [blogPostId]);

    useEffect(() => {
        if (content.title !== 'Blog') {
            document.title = `${content.title} - Mike Brauninger`;
            ReactGA.send({ hitType: 'pageview', page: window.location.pathname });
        }
    }, [content]);

    useEffect(() => {
        const fetchSuggestedPost = async () => {
            try {
                const response = await fetch(`${BASE_API_PATH}/feed?limit=10`);
                if (!response.ok) {
                    throw new Error('Could not fetch feed');
                }

                const data: FeedResponse = await response.json();
                const feedSuggestions = data.items
                    .filter((item) => !(item.contentType === 'blog' && item.id === blogPostId))
                    .slice(0, 3)
                    .map((item) => {
                        const isPhotoCollection = item.contentType === 'photo_collection';
                        return {
                            id: item.id,
                            title: item.title,
                            imageUrl: item.imageUrl,
                            description: item.summary,
                            category: isPhotoCollection
                                ? 'Photo Collection'
                                : inferCategoryFromTitleAndText(item.title, item.summary),
                            timestamp: item.timestamp,
                            href:
                                item.href?.replace(/^\/blog(?=\/|$)/, `/${BLOG_PATH}`) ||
                                (isPhotoCollection ? `/photos/${item.id}` : `/${BLOG_PATH}/post?id=${item.id}`),
                        };
                    });

                if (feedSuggestions.length > 0) {
                    setSuggestedPosts(feedSuggestions);
                    return;
                }
            } catch (error) {
                setSuggestedPosts([]);
            }
        };

        fetchSuggestedPost();
    }, [blogPostId]);

    return (
        <>
            <Helmet>
                <title>{content.title} - Mike Brauninger</title>
            </Helmet>
            <Box className={classes.centeredContent} sx={{ pb: { xs: 4, md: 6 } }}>
                {loadingPostContent ? (
                    <Box display={'flex'} alignItems={'center'} justifyContent={'center'}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Typography variant={'h3'} textAlign={'center'} color="#315a93">
                        {`Encountered error when retrieving data.`}
                    </Typography>
                ) : (
                    <Box
                        sx={{
                            width: '100%',
                            px: { xs: 1, md: 0 },
                        }}
                    >
                        <Typography
                            variant={window.innerWidth <= 768 ? 'h5' : 'h4'}
                            textAlign={'left'}
                            color="#163f86"
                            sx={{ pb: 0.75 }}
                        >
                            {content.title}
                        </Typography>
                        <Typography fontSize={18} paddingBottom={2} textAlign={'left'} color="#55729f">
                            {timestampToHumanReadableDate(content.timestamp)}
                        </Typography>
                        {content.body.map((element, index) =>
                            isImage(element) ? (
                                <ImageWithCaption
                                    key={`image-${element.mediaKey || element.imageUrl}-${index}`}
                                    url={element.imageUrl}
                                    caption={element.text}
                                />
                            ) : (
                                <Paragraph key={`paragraph-${index}`} text={element.text} />
                            ),
                        )}
                        <Divider sx={{ borderColor: '#d9e7f8', mb: 2.5, mt: 1 }} />
                        {suggestedPosts.length > 0 && (
                            <>
                                <Typography
                                    paddingBottom={2}
                                    fontWeight={'bold'}
                                    variant="h5"
                                    color="#163f86"
                                    textAlign="left"
                                >
                                    More Posts
                                </Typography>
                                <Hidden smDown>{suggestedArticles(suggestedPosts, 'row')}</Hidden>
                                <Hidden smUp>{suggestedArticles(suggestedPosts, 'column')}</Hidden>
                            </>
                        )}
                    </Box>
                )}
            </Box>
        </>
    );
}
